import React, { useState } from 'react';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTheme } from '../screens/ThemeContext';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../private/firebaseConfig';
import { useAuth } from '../private/AuthContext';
import CampayPaymentForm from '../services/CampayPaymentForm';

const SUBSCRIPTION_PLANS = [
  { id: 'five_days', name: '5 Jours', price: 15000, days: 5, color: '#4CAF50' },
  { id: 'fifteen_days', name: '15 Jours', price: 35000, days: 15, color: '#2196F3' },
  { id: 'thirty_days', name: '30 Jours', price: 100000, days: 30, color: '#9C27B0' },
  { id: 'three_months', name: '3 Mois', price: 300000, days: 90, color: '#FF9800' },
];

const paymentMethods = [
  { id: 'orange', name: 'Orange Money', icon: 'cellphone', color: '#FF6F00' },
  { id: 'mtn', name: 'Mobile Money', icon: 'cellphone', color: '#FFCA28' },
];

export default function AnnouncementPaymentScreen({ route, navigation }) {
  const { announcementData } = route.params;
  const { theme } = useTheme();
  const { user } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [showCampayForm, setShowCampayForm] = useState(false);

  const handlePaymentSuccess = async (paymentData) => {
    try {
      setLoading(true);
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + selectedPlan.days);
      await addDoc(collection(db, 'announcement-payments'), {
        userId: user.uid,
        amount: selectedPlan.price,
        planId: selectedPlan.id,
        planDuration: selectedPlan.days,
        paymentMethod: selectedMethod,
        paymentReference: paymentData?.paymentReference || 'direct-payment',
        status: 'completed',
        createdAt: serverTimestamp()
      });
      const announcementRef = await addDoc(collection(db, 'announcements'), {
        ...announcementData,
        createdAt: serverTimestamp(),
        status: 'active',
        etat: 'pending',
        subscriptionPlan: {
          planId: selectedPlan.id,
          duration: selectedPlan.days,
          price: selectedPlan.price,
          expirationDate: expirationDate,
        }
      });
      await addDoc(collection(db, 'notifications'), {
        userId: user.uid,
        announcementId: announcementRef.id,
        type: 'expiration_warning',
        message: `Votre annonce "${announcementData.titre}" va expirer le ${expirationDate.toLocaleDateString()}`,
        status: 'pending',
        scheduledFor: new Date(expirationDate.getTime() - (24 * 60 * 60 * 1000)),
        createdAt: serverTimestamp()
      });
      setSuccessModalVisible(true);
    } catch (error) {
      console.error('Error processing payment:', error);
      window.alert("Une erreur est survenue lors de l'enregistrement de votre annonce");
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentError = (error) => {
    console.error('Erreur de paiement:', error);
    window.alert('Une erreur est survenue lors du paiement. Veuillez réessayer.');
    setLoading(false);
  };

  const handlePayment = () => {
    if (!selectedPlan) {
      window.alert('Veuillez sélectionner un forfait');
      return;
    }
    if (!selectedMethod) {
      window.alert('Veuillez sélectionner une méthode de paiement');
      return;
    }
    setShowCampayForm(true);
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: theme.background,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '0',
    },
    header: {
      width: '100%',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      padding: '24px 24px 0 24px',
      backgroundColor: theme.background,
      position: 'sticky',
      top: 0,
      zIndex: 10,
    },
    backButton: {
      backgroundColor: theme.surface,
      borderRadius: '10px',
      padding: '8px',
      cursor: 'pointer',
      marginRight: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: `1px solid ${theme.border}`,
    },
    headerTitle: {
      fontSize: '22px',
      fontWeight: 'bold',
      color: theme.text,
    },
    content: {
      width: '100%',
      maxWidth: '600px',
      margin: '0 auto',
      padding: '24px',
      flex: 1,
      overflowY: 'auto',
      minHeight: 0,
      maxHeight: 'calc(100vh - 80px)', // header height approx
      boxSizing: 'border-box',
      paddingBottom: '48px', // pour ne pas masquer le bouton
    },
    plansContainer: {
      marginBottom: '32px',
    },
    planCard: (plan) => ({
      padding: '18px',
      borderRadius: '16px',
      border: `2px solid ${selectedPlan?.id === plan.id ? plan.color : theme.border}`,
      backgroundColor: theme.surface,
      marginBottom: '16px',
      cursor: 'pointer',
      position: 'relative',
      transition: 'border 0.2s',
    }),
    planHeader: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '8px',
    },
    planName: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: theme.text,
    },
    planPrice: (plan) => ({
      fontSize: '20px',
      fontWeight: 'bold',
      color: plan.color,
    }),
    planDuration: {
      fontSize: '14px',
      color: theme.textSecondary,
    },
    checkIcon: {
      position: 'absolute',
      right: '16px',
      top: '46px',
    },
    section: {
      padding: '20px',
      borderRadius: '16px',
      marginBottom: '24px',
      backgroundColor: theme.surface,
      border: `1px solid ${theme.border}`,
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: 'bold',
      marginBottom: '16px',
      color: theme.text,
    },
    methodsContainer: {
      display: 'flex',
      flexDirection: 'row',
      gap: '16px',
      flexWrap: 'wrap',
    },
    methodCard: (method) => ({
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      padding: '16px',
      borderRadius: '16px',
      border: `2px solid ${selectedMethod === method.id ? method.color : theme.border}`,
      backgroundColor: theme.surface,
      marginBottom: '12px',
      cursor: 'pointer',
      minWidth: '200px',
      flex: 1,
      transition: 'border 0.2s',
    }),
    methodIcon: (method) => ({
      width: '50px',
      height: '50px',
      borderRadius: '25px',
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: '16px',
      backgroundColor: method.color + '20',
      display: 'flex',
    }),
    methodName: {
      flex: 1,
      fontSize: '16px',
      fontWeight: '600',
      color: theme.text,
    },
    payButton: {
      width: '100%',
      padding: '16px',
      borderRadius: '12px',
      background: `linear-gradient(90deg, ${theme.primary}, ${theme.primaryDark || theme.primary})`,
      color: '#fff',
      fontSize: '16px',
      fontWeight: 'bold',
      border: 'none',
      cursor: loading ? 'not-allowed' : 'pointer',
      opacity: !selectedPlan || !selectedMethod || loading ? 0.5 : 1,
      marginTop: '32px',
      marginBottom: '32px',
      transition: 'opacity 0.2s',
    },
    modalOverlay: {
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    modalContent: {
      backgroundColor: theme.surface,
      borderRadius: '20px',
      padding: '32px',
      display: 'flex',
      alignItems: 'center',
      minWidth: '320px',
      maxWidth: '90vw',
    },
    successIcon: {
      width: '80px',
      height: '80px',
      borderRadius: '40px',
      backgroundColor: theme.primary + '20',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '16px',
    },
    modalTitle: {
      fontSize: '24px',
      fontWeight: 'bold',
      marginBottom: '8px',
      color: theme.text,
    },
    modalMessage: {
      fontSize: '16px',
      textAlign: 'center',
      marginBottom: '24px',
      color: theme.textSecondary,
    },
    modalButton: {
      padding: '12px 24px',
      borderRadius: '12px',
      backgroundColor: theme.primary,
      color: '#fff',
      fontSize: '16px',
      fontWeight: '600',
      border: 'none',
      cursor: 'pointer',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.backButton} onClick={() => {
          if (showCampayForm) setShowCampayForm(false);
          else navigation.goBack();
        }}>
          <MaterialCommunityIcons name="arrow-left" size={24} color={theme.primary} />
        </div>
        <span style={styles.headerTitle}>
          {showCampayForm 
            ? `Paiement via ${selectedMethod === 'mtn' ? 'Mobile Money' : 'Orange Money'}`
            : "Publication de l'annonce"}
        </span>
      </div>
      <div style={styles.content}>
        {!showCampayForm ? (
          <>
            {/* Forfaits */}
            <div style={styles.plansContainer}>
              <div style={styles.sectionTitle}>Choisissez votre forfait</div>
              {SUBSCRIPTION_PLANS.map((plan) => (
                <div
                  key={plan.id}
                  style={styles.planCard(plan)}
                  onClick={() => setSelectedPlan(plan)}
                >
                  <div style={styles.planHeader}>
                    <span style={styles.planName}>{plan.name}</span>
                    <span style={styles.planPrice(plan)}>{plan.price.toLocaleString()} XAF</span>
                  </div>
                  <div style={styles.planDuration}>Durée: {plan.days} jours</div>
                  {selectedPlan?.id === plan.id && (
                    <div style={styles.checkIcon}>
                      <MaterialCommunityIcons name="check-circle" size={24} color={plan.color} />
                    </div>
                  )}
                </div>
              ))}
            </div>
            {/* Méthodes de paiement */}
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Méthode de paiement</div>
              <div style={styles.methodsContainer}>
                {paymentMethods.map((method) => (
                  <div
                    key={method.id}
                    style={styles.methodCard(method)}
                    onClick={() => setSelectedMethod(method.id)}
                  >
                    <div style={styles.methodIcon(method)}>
                      <MaterialCommunityIcons name={method.icon} size={30} color={method.color} />
                    </div>
                    <span style={styles.methodName}>{method.name}</span>
                    {selectedMethod === method.id && (
                      <MaterialCommunityIcons name="check-circle" size={24} color={method.color} />
                    )}
                  </div>
                ))}
              </div>
            </div>
            <button
              style={styles.payButton}
              onClick={handlePayment}
              disabled={!selectedPlan || !selectedMethod || loading}
            >
              {loading ? 'Traitement...' : 'Continuer'}
            </button>
          </>
        ) : (
          <CampayPaymentForm 
            amount={selectedPlan.price}
            document={{
              id: 'announcement-' + Date.now(),
              type: 'announcement',
              titre: announcementData.titre || 'Annonce',
            }}
            documentDetail={{
              id: 'plan-' + selectedPlan.id,
              titre: `Forfait ${selectedPlan.name}`,
              prix: selectedPlan.price,
            }}
            userId={user.uid}
            onSuccess={handlePaymentSuccess}
            onError={handlePaymentError}
            theme={theme}
            paymentMethod={selectedMethod}
            navigation={navigation}
          />
        )}
      </div>
      {successModalVisible && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.successIcon}>
              <MaterialCommunityIcons name="check-circle" size={50} color={theme.primary} />
            </div>
            <div style={styles.modalTitle}>Publication réussie !</div>
            <div style={styles.modalMessage}>
              Votre annonce a été publiée avec succès pour une durée de {selectedPlan?.days} jours
            </div>
            <button
              style={styles.modalButton}
              onClick={() => {
                setSuccessModalVisible(false);
                navigation.navigate('TabNavigatorCentre');
              }}
            >Retour à l'accueil</button>
          </div>
        </div>
      )}
    </div>
  );
}